import { isNewSeries } from './showFields.js';

const normalizeLabel = (value) => {
  if (!value || typeof value !== 'string') {
    return '';
  }
  return value.trim();
};

const toTitleCase = (value) =>
  value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());

const formatPercentage = (count, total) => (total > 0 ? count / total : 0);

const parseReleaseDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }
  return new Date(year, month - 1, day);
};

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildDateRange = (startDate, endDate) => {
  const result = [];
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    return result;
  }
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || startDate > endDate) {
    return result;
  }
  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  while (cursor <= endDate) {
    const iso = toIsoDate(cursor);
    if (iso) {
      result.push(iso);
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};

export const calculateTrend = (currentValue, previousValue) => {
  const current = typeof currentValue === 'number' ? currentValue : 0;
  const hasPrevious = typeof previousValue === 'number';
  const previous = hasPrevious ? previousValue : null;
  if (!hasPrevious) {
    return {
      current,
      previous,
      delta: null,
      percent: null,
      direction: 'unknown',
    };
  }
  const delta = current - previous;
  const percent = previous > 0 ? Number(((delta / previous) * 100).toFixed(1)) : null;
  const direction = delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down';

  return {
    current,
    previous,
    delta,
    percent,
    direction,
  };
};

const sortBuckets = (buckets) =>
  buckets.sort((a, b) => {
    if (b.count === a.count) {
      return a.label.localeCompare(b.label);
    }
    return b.count - a.count;
  });

export const getNewReturningBreakdown = (releases = []) => {
  if (!Array.isArray(releases) || releases.length === 0) {
    return {
      total: 0,
      newCount: 0,
      returningCount: 0,
      percentageNew: 0,
      percentageReturning: 0,
    };
  }

  let newCount = 0;
  releases.forEach((release) => {
    if (release && isNewSeries(release)) {
      newCount += 1;
    }
  });

  const total = releases.length;
  const returningCount = total - newCount;

  return {
    total,
    newCount,
    returningCount,
    percentageNew: formatPercentage(newCount, total),
    percentageReturning: formatPercentage(returningCount, total),
  };
};

export const getGenreBuckets = (releases = []) => {
  if (!Array.isArray(releases) || releases.length === 0) {
    return [];
  }

  const map = new Map();
  let totalAssignments = 0;

  releases.forEach((release) => {
    const genres = Array.isArray(release?.genres) ? release.genres : [];
    genres.forEach((genre) => {
      const normalized = normalizeLabel(genre);
      if (!normalized) {
        return;
      }
      totalAssignments += 1;
      const key = normalized.toLowerCase();
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(key, { label: normalized, count: 1 });
      }
    });
  });

  const buckets = sortBuckets(Array.from(map.values()));
  return buckets.map((bucket) => ({
    ...bucket,
    percentage: formatPercentage(bucket.count, totalAssignments),
  }));
};

export const getNetworkBuckets = (releases = []) => {
  if (!Array.isArray(releases) || releases.length === 0) {
    return [];
  }

  const map = new Map();
  let totalAssignments = 0;

  releases.forEach((release) => {
    const networkValues = new Set();

    if (Array.isArray(release?.networks)) {
      release.networks.forEach((network) => {
        const normalized = normalizeLabel(network);
        if (normalized) {
          networkValues.add(normalized);
        }
      });
    }

    const sourceName = normalizeLabel(release?.sourceName);
    if (sourceName) {
      networkValues.add(sourceName);
    }

    networkValues.forEach((network) => {
      totalAssignments += 1;
      const key = network.toLowerCase();
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(key, { label: network, count: 1 });
      }
    });
  });

  const buckets = sortBuckets(Array.from(map.values()));
  return buckets.map((bucket) => ({
    ...bucket,
    percentage: formatPercentage(bucket.count, totalAssignments),
  }));
};

const SOURCE_LABELS = {
  watchmode: 'Watchmode',
  tvmaze: 'TVMaze',
  tvmaze_selective: 'TVMaze (Selective)',
  unknown: 'Unknown',
};

const formatSourceLabel = (sourceKey) => {
  if (!sourceKey) {
    return SOURCE_LABELS.unknown;
  }
  const normalized = sourceKey.toLowerCase();
  if (SOURCE_LABELS[normalized]) {
    return SOURCE_LABELS[normalized];
  }
  return toTitleCase(sourceKey.replace(/_/g, ' '));
};

export const getSourceBreakdown = (releases = []) => {
  if (!Array.isArray(releases) || releases.length === 0) {
    return [];
  }

  const map = new Map();

  releases.forEach((release) => {
    const rawSource = normalizeLabel(release?.source) || 'unknown';
    const key = rawSource.toLowerCase();
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(key, {
        key,
        label: formatSourceLabel(rawSource),
        count: 1,
      });
    }
  });

  const total = releases.length;
  const buckets = sortBuckets(Array.from(map.values()));
  return buckets.map((bucket) => ({
    ...bucket,
    percentage: formatPercentage(bucket.count, total),
  }));
};

export const getRatingSummary = (releases = []) => {
  if (!Array.isArray(releases) || releases.length === 0) {
    return {
      count: 0,
      ratedCount: 0,
      unratedCount: 0,
      average: null,
      median: null,
      min: null,
      max: null,
      distribution: {
        '9+': 0,
        '8-8.9': 0,
        '7-7.9': 0,
        '<7': 0,
        unrated: 0,
      },
    };
  }

  const distribution = {
    '9+': 0,
    '8-8.9': 0,
    '7-7.9': 0,
    '<7': 0,
    unrated: 0,
  };

  const ratings = [];
  let unratedCount = 0;

  releases.forEach((release) => {
    const rating = typeof release?.rating === 'number' ? release.rating : null;
    if (rating === null) {
      unratedCount += 1;
      distribution.unrated += 1;
      return;
    }

    ratings.push(rating);

    if (rating >= 9) {
      distribution['9+'] += 1;
    } else if (rating >= 8) {
      distribution['8-8.9'] += 1;
    } else if (rating >= 7) {
      distribution['7-7.9'] += 1;
    } else {
      distribution['<7'] += 1;
    }
  });

  ratings.sort((a, b) => a - b);
  const ratedCount = ratings.length;

  const average = ratedCount
    ? Number((ratings.reduce((sum, value) => sum + value, 0) / ratedCount).toFixed(2))
    : null;

  const median = ratedCount
    ? ratedCount % 2 === 1
      ? ratings[(ratedCount - 1) / 2]
      : Number(((ratings[ratedCount / 2 - 1] + ratings[ratedCount / 2]) / 2).toFixed(2))
    : null;

  const min = ratedCount ? ratings[0] : null;
  const max = ratedCount ? ratings[ratedCount - 1] : null;

  return {
    count: releases.length,
    ratedCount,
    unratedCount,
    average,
    median,
    min,
    max,
    distribution,
  };
};

export const getRatingDistributionBuckets = (ratingSummary) => {
  if (!ratingSummary || !ratingSummary.distribution) {
    return [];
  }

  const total = Object.values(ratingSummary.distribution).reduce((acc, value) => acc + value, 0);
  return Object.entries(ratingSummary.distribution).map(([label, value]) => ({
    label,
    value,
    percentage: formatPercentage(value, total),
  }));
};

export const getDailyReleaseVolume = (releases = [], { startDate = null, endDate = null } = {}) => {
  if (!Array.isArray(releases) || releases.length === 0) {
    return [];
  }

  const countByDate = new Map();
  let derivedStart = null;
  let derivedEnd = null;

  releases.forEach((release) => {
    const releaseDate = parseReleaseDate(release?.releaseDate);
    if (!releaseDate) {
      return;
    }
    const iso = toIsoDate(releaseDate);
    if (!iso) {
      return;
    }
    countByDate.set(iso, (countByDate.get(iso) || 0) + 1);

    if (!derivedStart || releaseDate < derivedStart) {
      derivedStart = releaseDate;
    }
    if (!derivedEnd || releaseDate > derivedEnd) {
      derivedEnd = releaseDate;
    }
  });

  if (countByDate.size === 0) {
    return [];
  }

  const normalizedStart = startDate instanceof Date && !Number.isNaN(startDate.getTime())
    ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
    : derivedStart;
  const normalizedEnd = endDate instanceof Date && !Number.isNaN(endDate.getTime())
    ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
    : derivedEnd;

  const dayFormatter = new Intl.DateTimeFormat('en', { weekday: 'short' });
  const range = buildDateRange(normalizedStart, normalizedEnd);
  return range.map((iso) => {
    const date = parseReleaseDate(iso);
    return {
      date: iso,
      dayLabel: date ? dayFormatter.format(date) : '',
      count: countByDate.get(iso) || 0,
    };
  });
};

export const getTopRatedUpcoming = (releases = [], limit = 5) => {
  if (!Array.isArray(releases) || releases.length === 0 || limit <= 0) {
    return [];
  }

  return releases
    .filter((release) => typeof release?.rating === 'number')
    .sort((a, b) => {
      if (b.rating !== a.rating) {
        return b.rating - a.rating;
      }
      const dateA = typeof a?.releaseDate === 'string' ? a.releaseDate : '';
      const dateB = typeof b?.releaseDate === 'string' ? b.releaseDate : '';
      if (dateA !== dateB) {
        return dateA.localeCompare(dateB);
      }
      const titleA = typeof a?.title === 'string' ? a.title : '';
      const titleB = typeof b?.title === 'string' ? b.title : '';
      return titleA.localeCompare(titleB);
    })
    .slice(0, limit)
    .map((release) => ({
      id: release?.id ?? null,
      slug: release?.slug ?? null,
      title: release?.title || 'Untitled',
      rating: release.rating,
      releaseDate: release?.releaseDate || null,
      network: release?.sourceName || (Array.isArray(release?.networks) ? release.networks[0] : null),
    }));
};

export default {
  getNewReturningBreakdown,
  getGenreBuckets,
  getNetworkBuckets,
  getSourceBreakdown,
  getRatingSummary,
  getRatingDistributionBuckets,
  getDailyReleaseVolume,
  getTopRatedUpcoming,
  calculateTrend,
};
